import { isAnswered, type ReportAnswer, type ReportAnswers, type ReportFinding } from './report-checks.js'

/**
 * Station and location checks: the questions a form asks once per station.
 *
 * A form section of kind `STATIONS` names a station type — lockable rat
 * stations, in-ground termite stations, inspection locations — and its
 * questions are asked of every station of that type at the job's site. The
 * stations belong to the site and outlive the visit; the questions belong to
 * the form. This is how ServiceM8's asset forms worked (one response per
 * station, 9,635 of them in the import) folded into the one report the
 * service produces. ADR 029.
 *
 * In the filler's answer map a station answer is keyed `fieldKey@assetId`,
 * so the device draft, the debounced save and the review all keep working on
 * one `Record<string, answer>`. This module is the only place that spells the
 * key.
 */

export const STATION_KEY_SEPARATOR = '@'

/** The key a station answer is held under: `consumedBy@cm1abc…`. */
export function stationAnswerKey(fieldKey: string, assetId: string): string {
  return `${fieldKey}${STATION_KEY_SEPARATOR}${assetId}`
}

/** The field and station a key names, or null for an ordinary question's key. */
export function parseStationAnswerKey(
  key: string,
): { fieldKey: string; assetId: string } | null {
  const at = key.lastIndexOf(STATION_KEY_SEPARATOR)
  if (at <= 0 || at === key.length - 1) return null
  return { fieldKey: key.slice(0, at), assetId: key.slice(at + 1) }
}

/**
 * Reads `FormField.alertValues` without trusting it: a list of option values
 * whose presence means something needs attention.
 */
export function parseAlertValues(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

/** Whether an answer contains one of the values that flag attention. */
export function isAlertAnswer(value: ReportAnswer, alertValues: readonly string[]): boolean {
  if (alertValues.length === 0 || !isAnswered(value)) return false
  if (Array.isArray(value)) return value.some((item) => alertValues.includes(item))
  return alertValues.includes(String(value))
}

/** The parts of a station question this module needs. */
export type StationQuestion = {
  key: string
  label: string
  type: string
  alertValues?: unknown
}

export type StationRef = { id: string; name: string }

export type StationState = 'unchecked' | 'partial' | 'clear' | 'alert'

/**
 * Where one station stands on this report.
 *
 * `alert` wins over everything: a station with activity recorded is the line
 * the client reads first, even if a question beside it is blank. Photo
 * questions count as answered only by their photos, which the caller passes
 * through `answers` as attachment ids like any photo field.
 */
export function stationState(
  questions: StationQuestion[],
  answers: ReportAnswers,
  assetId: string,
): StationState {
  const asked = questions.filter((question) => question.type !== 'HEADING')
  if (asked.length === 0) return 'unchecked'

  let answered = 0
  let alert = false

  for (const question of asked) {
    const value = answers[stationAnswerKey(question.key, assetId)]
    if (isAnswered(value)) answered += 1
    if (isAlertAnswer(value, parseAlertValues(question.alertValues))) alert = true
  }

  if (alert) return 'alert'
  if (answered === 0) return 'unchecked'
  // A photo question is the usual one left blank on a clear station, and a
  // clear station with no photo inside it is still checked.
  const required = asked.filter((question) => question.type !== 'PHOTO')
  const requiredAnswered = required.filter((question) =>
    isAnswered(answers[stationAnswerKey(question.key, assetId)]),
  ).length
  return requiredAnswered >= required.length ? 'clear' : 'partial'
}

export type StationTally = {
  total: number
  /** Stations with anything recorded against them. */
  checked: number
  /** Stations with an answer that needs attention. */
  alert: number
  /** Nothing recorded. */
  unchecked: StationRef[]
  /** Something recorded, an ordinary question still blank. */
  incomplete: StationRef[]
}

/** The figures a station section reports: how many, how many checked, how many need attention. */
export function tallyStations(
  questions: StationQuestion[],
  stations: StationRef[],
  answers: ReportAnswers,
): StationTally {
  let checked = 0
  let alert = 0
  const unchecked: StationRef[] = []
  const incomplete: StationRef[] = []

  for (const station of stations) {
    const state = stationState(questions, answers, station.id)
    if (state === 'alert') alert += 1
    if (state === 'unchecked') unchecked.push(station)
    else checked += 1
    if (state === 'partial') incomplete.push(station)
  }

  return { total: stations.length, checked, alert, unchecked, incomplete }
}

const listed = (stations: StationRef[]) => {
  const names = stations.map((station) => station.name)
  return names.length <= 6 ? names.join(', ') : `${names.slice(0, 6).join(', ')} and ${names.length - 6} more`
}

export type StationSectionSpec = {
  key: string
  name: string
  questions: StationQuestion[]
  stations: StationRef[]
}

/**
 * The check sheet's lines for station sections: one per section with
 * stations left unchecked, naming them.
 *
 * One line rather than one per station per question. Fifteen stations with
 * three questions each is forty-five blanks, and a sheet that long is one
 * nobody reads. The jump goes to the first station still owed.
 */
export function reviewStations(
  sections: StationSectionSpec[],
  answers: ReportAnswers,
): ReportFinding[] {
  const findings: ReportFinding[] = []

  for (const section of sections) {
    const first = section.questions.find((question) => question.type !== 'HEADING')
    if (!first) continue

    const { unchecked, incomplete } = tallyStations(section.questions, section.stations, answers)

    if (unchecked.length > 0) {
      findings.push({
        group: 'BLANK',
        fieldKey: stationAnswerKey(first.key, unchecked[0]!.id),
        title:
          unchecked.length === 1
            ? `${section.name}: one station not checked`
            : `${section.name}: ${unchecked.length} stations not checked`,
        detail: listed(unchecked),
      })
    }

    if (incomplete.length > 0) {
      findings.push({
        group: 'BLANK',
        fieldKey: stationAnswerKey(first.key, incomplete[0]!.id),
        title:
          incomplete.length === 1
            ? `${section.name}: one station has a question left blank`
            : `${section.name}: ${incomplete.length} stations have a question left blank`,
        detail: listed(incomplete),
      })
    }
  }

  return findings
}

/**
 * The next label for a station added on site: "R" and R1–R7 already there
 * gives R8. Labels that do not follow the pattern are left alone rather than
 * guessed at.
 */
export function nextStationName(prefix: string | null | undefined, existing: string[]): string {
  const stem = (prefix ?? '').trim()
  const pattern = new RegExp(`^${stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(\\d+)$`, 'i')
  let highest = 0

  for (const name of existing) {
    const match = pattern.exec(name.trim())
    if (match) highest = Math.max(highest, Number(match[1]))
  }

  return `${stem}${highest + 1}`
}
